import React from 'react';

import { Provider } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import { Switch, Route, Redirect } from 'react-router';

import { ThemeProvider } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import { ToastContainer } from 'react-toastify';

import { store, history } from './state-mgmt/store';
import { ROUTES } from '../constants';
import { theme } from '../constants/style';

import ProtectedRoute from './views/shared/ProtectedRoute';
import PublicRoute from './views/shared/PublicRoute';
import ToastList from './views/shared/ToastList';
import Register from './views/pages/Register';
import ForgotPassword from './views/pages/ForgotPassword';
import ResetPassword from './views/pages/ResetPassword';
import ValidateUser from './views/pages/ValidateUser';
import ApproveUser from './views/pages/ApproveUser';
import DenyUser from './views/pages/DenyUser';
import EmergencyReport from './views/shared/EmergencyReport';
import CacheBuster from '../CacheBuster';
import DownloadsNotifications from './views/shared/DownloadsNotifications';
import SentrySetup from '../SentrySetup';
import { UserModel } from './models';
import MicroFrontendWrapper from './views/shared/MicroFrontendWrapper/MicroFrontendWrapper';
import 'react-toastify/dist/ReactToastify.css';
import './views/shared/EmergencyReport/index.css';

const Dashboard = React.lazy(() => import('./views/pages/Dashboard'));
const DashboardProject = React.lazy(() => import('./views/pages/DashboardProject'));
const DashboardClient = React.lazy(() => import('./views/pages/DashboardClient'));
const DashboardWorker = React.lazy(() => import('./views/pages/DashboardWorker'));
const DashboardWfmExpanded = React.lazy(() => import('./views/pages/WfmExpanded'));
const DailyLogExpanded = React.lazy(() => import('./views/pages/DailyLogExpanded'));
const ClientList = React.lazy(() => import('./views/pages/ClientList'));
const ClientInvite = React.lazy(() => import('./views/pages/ClientInvite'));
const ClientWizard = React.lazy(() => import('./views/pages/ClientWizard'));
const ClientDetail = React.lazy(() => import('./views/pages/ClientDetail'));
const ProjectList = React.lazy(() => import('./views/pages/ProjectList'));
const ProjectWizard = React.lazy(() => import('./views/pages/ProjectWizardNew'));
const ProjectDetailNew = React.lazy(() => import('./views/pages/ProjectDetailNew'));
const ProcoreMapping = React.lazy(() => import('./views/pages/ProcoreClientMapping'));
const ProjectInvitation = React.lazy(() => import('./views/pages/ProjectInvitation'));
const InventoryList = React.lazy(() => import('./views/pages/InventoryList'));
const AccessControlSystemWizard = React.lazy(() => import('./views/pages/AccessControlSystemWizard'));
const BadgePrinterSystemWizard = React.lazy(() => import('./views/pages/BadgePrinterSystemWizard'));
const WorkerWizard = React.lazy(() => import('./views/pages/WorkerWizard'));
const WorkerList = React.lazy(() => import('./views/pages/WorkerList'));
const WorkerDetail = React.lazy(() => import('./views/pages/WorkerDetail'));
const InvoiceList = React.lazy(() => import('./views/pages/InvoiceList'));
const InvoiceDetail = React.lazy(() => import('./views/pages/InvoiceDetail'));
const Security = React.lazy(() => import('./views/pages/Security'));
const TabletExperience = React.lazy(() => import('./views/pages/TabletExperience'));
const AdminSettings = React.lazy(() => import('./views/pages/AdminSettings'));
const ProcoreCompanies = React.lazy(() => import('./views/pages/Procore'));
const AccountSettings = React.lazy(() => import('./views/pages/AccountSettings'));
const PaymentSettings = React.lazy(() => import('./views/pages/PaymentSettings'));
const CompanyTree = React.lazy(() => import('./views/pages/CompanyTree'));
const ClientOnboarding = React.lazy(() => import('./views/pages/ClientOnboarding'));
const Learning = React.lazy(() => import('./views/pages/Learning'));
const ManageFcaUsers = React.lazy(() => import('./views/pages/ManageFcaUsers'));
const UnAuthorized = React.lazy(() => import('./views/pages/UnAuthorized'));
const ProductCatalog = React.lazy(() => import('./views/pages/ProductCatalog'));
const AuditTrail = React.lazy(() => import('./views/pages/AuditTrail'));
const ProductWizard = React.lazy(() => import('./views/pages/ProductWizard'));
const Downloads = React.lazy(() => import('./views/pages/Downloads'));

// Micro-front-ends
const ReportsApp = React.lazy(() => import('@fca/reports/App'));

const AppRoot = () => {
  // useEffect(() => {
  //   // history.listen(() => {
  //   //   if (globalThis.swUpdateReady) {
  //   //     globalThis.swUpdateReady = false;
  //   //     window.stop();
  //   //     window.location.reload();
  //   //   }
  //   // });
  // }, []);

  return (
    <Provider store={store}>
      <ConnectedRouter history={history}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <ToastContainer
            limit={100}
            position="bottom-right"
            autoClose={false}
            closeOnClick={false}
            newestOnTop={false}
            pauseOnFocusLoss={true}
            draggable={true}
            theme="light"
          />
          <EmergencyReport />
          <CacheBuster />
          <DownloadsNotifications />
          {process.env.REACT_APP_SENTRY_ENABLED === 'true' && <SentrySetup />}
          <Switch>
            <ProtectedRoute permissionsExpression={ROUTES.DASHBOARD.permissionsExpression} exact={true} path={ROUTES.DASHBOARD.path} render={Dashboard} />
            <ProtectedRoute
              permissionsExpression={ROUTES.DASHBOARD_PROJECTS.permissionsExpression}
              exact={true}
              path={ROUTES.DASHBOARD_PROJECTS.path}
              render={DashboardProject}
            />

            <ProtectedRoute
              permissionsExpression={ROUTES.DASHBOARD_CLIENTS.permissionsExpression}
              exact={true}
              path={ROUTES.DASHBOARD_CLIENTS.path}
              render={DashboardClient}
            />

            <ProtectedRoute
              permissionsExpression={ROUTES.DASHBOARD_WORKERS.permissionsExpression}
              exact={true}
              path={ROUTES.DASHBOARD_WORKERS.path}
              render={DashboardWorker}
            />

            <ProtectedRoute
              permissionsExpression={ROUTES.DASHBOARD_WFM_EXPANDED.permissionsExpression}
              exact={true}
              path={ROUTES.DASHBOARD_WFM_EXPANDED.path}
              render={DashboardWfmExpanded}
            />

            <ProtectedRoute
              permissionsExpression={ROUTES.DASHBOARD_DAILY_LOG_EXPANDED.permissionsExpression}
              exact={true}
              path={ROUTES.DASHBOARD_DAILY_LOG_EXPANDED.path}
              render={DailyLogExpanded}
            />

            <ProtectedRoute permissionsExpression={ROUTES.DOWNLOADS.permissionsExpression} exact={true} path={ROUTES.DOWNLOADS.path} render={Downloads} />

            <ProtectedRoute
              permissionsExpression={ROUTES.CLIENT_INVITE.permissionsExpression}
              exact={true}
              path={ROUTES.CLIENT_INVITE.path}
              render={ClientInvite}
            />

            <ProtectedRoute
              permissionsExpression={ROUTES.CLIENT_WIZARD.permissionsExpression}
              exact={true}
              path={ROUTES.CLIENT_WIZARD.path}
              render={ClientWizard}
            />

            <ProtectedRoute
              exact={true}
              render={ClientDetail}
              path={ROUTES.CLIENT_DETAIL.path}
              tabMap={ROUTES.CLIENT_DETAIL.tabMap}
              shouldBeFcaUser={ROUTES.CLIENT_DETAIL.shouldBeFcaUser}
              permissionsExpression={ROUTES.CLIENT_DETAIL.permissionsExpression}
              includeScopedPermissions={ROUTES.CLIENT_DETAIL.includeScopedPermissions}
            />

            <ProtectedRoute
              exact={true}
              render={ClientList}
              path={ROUTES.CLIENT_LIST.path}
              shouldBeFcaUser={ROUTES.CLIENT_LIST.shouldBeFcaUser}
              permissionsExpression={ROUTES.CLIENT_LIST.permissionsExpression}
              includeScopedPermissions={ROUTES.CLIENT_LIST.includeScopedPermissions}
            />

            <ProtectedRoute
              permissionsExpression={ROUTES.PROJECT_LIST.permissionsExpression}
              includeScopedPermissions={ROUTES.PROJECT_LIST.includeScopedPermissions}
              exact={true}
              path={ROUTES.PROJECT_LIST.path}
              render={ProjectList}
            />
            <ProtectedRoute
              permissionsExpression={ROUTES.PROJECT_DETAIL.permissionsExpression}
              exact={true}
              path={ROUTES.PROJECT_DETAIL.path}
              render={ProjectDetailNew}
              tabMap={ROUTES.PROJECT_DETAIL.tabMap}
            />
            <ProtectedRoute
              permissionsExpression={ROUTES.PROCORE_MAPPING.permissionsExpression}
              exact={true}
              path={ROUTES.PROCORE_MAPPING.path}
              render={ProcoreMapping}
            />
            <ProtectedRoute
              permissionsExpression={ROUTES.PROJECT_WIZARD.permissionsExpression}
              exact={true}
              path={ROUTES.PROJECT_WIZARD.path}
              render={ProjectWizard}
            />
            <ProtectedRoute
              permissionsExpression={ROUTES.PROJECT_INVITATION.permissionsExpression}
              exact={true}
              path={ROUTES.PROJECT_INVITATION.path}
              render={ProjectInvitation}
            />
            <ProtectedRoute
              permissionsExpression={ROUTES.INVENTORY_LIST.permissionsExpression}
              exact={true}
              path={ROUTES.INVENTORY_LIST.path}
              render={InventoryList}
            />
            <ProtectedRoute permissionsExpression={ROUTES.ADMIN.permissionsExpression} exact={true} path={ROUTES.ADMIN.path} render={AdminSettings} />
            <ProtectedRoute
              permissionsExpression={ROUTES.PROCORE_CLIENTS.permissionsExpression}
              exact={true}
              path={ROUTES.PROCORE_CLIENTS.path}
              render={ProcoreCompanies}
            />
            <ProtectedRoute
              permissionsExpression={ROUTES.PROCORE_MAPPING.permissionsExpression}
              exact={true}
              path={ROUTES.PROCORE_MAPPING.path}
              render={ProcoreCompanies}
            />

            <ProtectedRoute
              permissionsExpression={ROUTES.ACCESS_CONTROL_SYSTEM_WIZARD.permissionsExpression}
              exact={true}
              path={ROUTES.ACCESS_CONTROL_SYSTEM_WIZARD.path}
              render={AccessControlSystemWizard}
            />
            <ProtectedRoute
              permissionsExpression={ROUTES.BADGE_PRINTER_SYSTEM_WIZARD.permissionsExpression}
              exact={true}
              path={ROUTES.BADGE_PRINTER_SYSTEM_WIZARD.path}
              render={BadgePrinterSystemWizard}
            />
            <ProtectedRoute
              permissionsExpression={ROUTES.WORKER_LIST.permissionsExpression}
              includeScopedPermissions={ROUTES.WORKER_LIST.includeScopedPermissions}
              exact={true}
              path={ROUTES.WORKER_LIST.path}
              render={WorkerList}
            />
            <ProtectedRoute
              permissionsExpression={ROUTES.WORKER_WIZARD.permissionsExpression}
              includeScopedPermissions={ROUTES.WORKER_WIZARD.includeScopedPermissions}
              exact={true}
              path={ROUTES.WORKER_WIZARD.path}
              render={WorkerWizard}
            />
            <ProtectedRoute
              permissionsExpression={ROUTES.WORKER_DETAIL.permissionsExpression}
              includeScopedPermissions={ROUTES.WORKER_DETAIL.includeScopedPermissions}
              exact={true}
              path={ROUTES.WORKER_DETAIL.path}
              render={WorkerDetail}
              tabMap={ROUTES.WORKER_DETAIL.tabMap}
            />
            <ProtectedRoute
              permissionsExpression={ROUTES.INVOICE_LIST.permissionsExpression}
              includeScopedPermissions={ROUTES.INVOICE_LIST.includeScopedPermissions}
              exact={true}
              path={ROUTES.INVOICE_LIST.path}
              render={InvoiceList}
            />
            <ProtectedRoute
              permissionsExpression={ROUTES.INVOICE_DETAIL.permissionsExpression}
              includeScopedPermissions={ROUTES.INVOICE_DETAIL.includeScopedPermissions}
              exact={true}
              path={ROUTES.INVOICE_DETAIL.path}
              render={InvoiceDetail}
            />
            <ProtectedRoute
              shouldBeFcaUser={ROUTES.SECURITY.shouldBeFcaUser}
              permissionsExpression={ROUTES.SECURITY.permissionsExpression}
              exact={true}
              path={ROUTES.SECURITY.path}
              render={Security}
            />
            <ProtectedRoute
              permissionsExpression={ROUTES.TABLET_EXPERIENCE.permissionsExpression}
              includeScopedPermissions={ROUTES.TABLET_EXPERIENCE.includeScopedPermissions}
              exact={true}
              path={ROUTES.TABLET_EXPERIENCE.path}
              render={TabletExperience}
            />
            <ProtectedRoute
              permissionsExpression={ROUTES.ACCOUNT_SETTINGS.permissionsExpression}
              path={ROUTES.ACCOUNT_SETTINGS.path}
              exact={true}
              render={AccountSettings}
            />
            <ProtectedRoute
              permissionsExpression={ROUTES.PAYMENT_SETTINGS.permissionsExpression}
              path={ROUTES.PAYMENT_SETTINGS.path}
              exact={true}
              render={PaymentSettings}
            />
            <ProtectedRoute
              permissionsExpression={ROUTES.COMPANY_TREE.permissionsExpression}
              path={ROUTES.COMPANY_TREE.path}
              exact={true}
              render={CompanyTree}
            />
            <ProtectedRoute
              permissionsExpression={ROUTES.CLIENT_ONBOARDING.permissionsExpression}
              exact={true}
              path={ROUTES.CLIENT_ONBOARDING.path}
              render={ClientOnboarding}
            />
            {/* <ProtectedRoute
              permissionsExpression={ROUTES.TABLET_EXPERIENCE.permissionsExpression}
              exact={true}
              path={ROUTES.TABLET_EXPERIENCE.path}
              render={TabletExperience}
            /> */}
            <ProtectedRoute permissionsExpression={ROUTES.LEARNING.permissionsExpression} exact={true} path={ROUTES.LEARNING.path} render={Learning} />
            <ProtectedRoute
              permissionsExpression={ROUTES.MANAGE_FCA_USERS.permissionsExpression}
              exact={true}
              path={ROUTES.MANAGE_FCA_USERS.path}
              render={ManageFcaUsers}
              shouldBeFcaUser={true}
            />
            <ProtectedRoute
              permissionsExpression={ROUTES.PRODUCT_CATALOG.permissionsExpression}
              exact={true}
              path={ROUTES.PRODUCT_CATALOG.path}
              render={ProductCatalog}
              shouldBeFcaUser={true}
            />
            <ProtectedRoute
              permissionsExpression={ROUTES.AUDIT_TRAIL.permissionsExpression}
              exact={true}
              path={ROUTES.AUDIT_TRAIL.path}
              render={AuditTrail}
              shouldBeFcaUser={true}
            />
            <ProtectedRoute
              permissionsExpression={ROUTES.PRODUCT_WIZARD.permissionsExpression}
              exact={true}
              path={ROUTES.PRODUCT_WIZARD.path}
              render={ProductWizard}
            />
            <ProtectedRoute
              permissionsExpression={ROUTES.REPORTS_APP.permissionsExpression}
              exact={false}
              path={ROUTES.REPORTS_APP.path}
              passAuthInfo={true}
              render={props => (
                <MicroFrontendWrapper fallback={<h2>Something Went Wrong!</h2>}>
                  <ReportsApp authInfo={props.authInfo} />
                </MicroFrontendWrapper>
              )}
              shouldBeFcaUser={true}
            />

            <ProtectedRoute path={ROUTES.APPROVE_USER.path} exact={true} render={() => <ApproveUser />} />
            <ProtectedRoute path={ROUTES.DENY_USER.path} exact={true} render={() => <DenyUser />} />

            <ProtectedRoute path={ROUTES.UNAUTHORIZED.path} exact={true} render={() => <UnAuthorized />} />

            <PublicRoute path={ROUTES.REGISTER.path} exact={true} render={() => <Register />} />
            <PublicRoute path={ROUTES.FORGOT_PASSWORD.path} exact={true} render={() => <ForgotPassword />} />
            <PublicRoute path={ROUTES.RESET_PASSWORD.path} exact={true} render={() => <ResetPassword />} />
            <PublicRoute path={ROUTES.VALIDATE_USER.path} exact={true} render={() => <ValidateUser />} />
            <Route path="*" render={() => <Redirect to="/" />} />
          </Switch>
        </ThemeProvider>
      </ConnectedRouter>
      <ToastList />
    </Provider>
  );
};

export default AppRoot;
